import React, { createContext, useContext, useEffect, useState } from 'react';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};

// Enhanced and robust translation service
class TranslationService {
  constructor() {
    this.cache = new Map();
    this.isTranslating = false;
    this.retryAttempts = 0;
    this.maxRetries = 3;
    this.apiEndpoints = [
      // Primary endpoint - Google Translate API (free tier)
      'https://translate.googleapis.com/translate_a/single?client=gtx',
      // Fallback endpoints
      'https://translate.googleapis.com/translate_a/single?client=webapp',
      'https://translate.googleapis.com/translate_a/single?client=t'
    ];
    this.currentEndpointIndex = 0;
    this.excludeSelectors = [
      'script', 'style', 'noscript', 'meta', 'link', 'head',
      '[data-translate="false"]', '.no-translate', '[translate="no"]',
      'code', 'pre', '.code-block', '[data-translatable="false"]'
    ];
    this.observerActive = false;
    this.translationQueue = [];
    this.isProcessingQueue = false;
  }

  async translateText(text, targetLanguage, sourceLanguage = 'auto') {
    if (!text?.trim() || text?.length < 2) return text;
    
    // Skip if text is mostly numbers, symbols, or already in target language
    if (/^[\d\s\W]+$/u?.test(text) || this.isTranslatable(text, targetLanguage)) {
      return text;
    }
    
    const cacheKey = `${text}_${sourceLanguage}_${targetLanguage}`;
    if (this.cache?.has(cacheKey)) {
      return this.cache?.get(cacheKey);
    }

    let lastError = null;
    
    // Try all API endpoints
    for (let i = 0; i < this.apiEndpoints?.length; i++) {
      const endpoint = this.apiEndpoints?.[i];
      
      try {
        const url = `${endpoint}&sl=${sourceLanguage}&tl=${targetLanguage?.toLowerCase()}&dt=t&q=${encodeURIComponent(text)}`;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller?.abort(), 10000); // 10s timeout
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          },
          signal: controller?.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response?.ok) {
          throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
        }
        
        const data = await response?.json();
        
        if (data?.[0]?.[0]?.[0]) {
          const translatedText = data?.[0]?.[0]?.[0];
          this.cache?.set(cacheKey, translatedText);
          this.retryAttempts = 0; // Reset on success
          return translatedText;
        }
        
        throw new Error('Invalid response format');
        
      } catch (error) {
        console.warn(`Translation attempt ${i + 1} failed:`, error?.message);
        lastError = error;
        
        if (error?.name === 'AbortError') {
          console.warn('Translation request timed out');
        }
        
        // Wait before trying next endpoint
        if (i < this.apiEndpoints?.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    // All endpoints failed
    console.error('All translation endpoints failed:', lastError);
    this.retryAttempts++;
    
    // Return original text on failure
    return text;
  }

  isTranslatable(text, targetLanguage) {
    // Skip common untranslatable patterns
    const untranslatablePatterns = [
      /^[A-Z]{2,}$/,  // All caps abbreviations
      /^\d+([.,]\d+)?[%]?$/,  // Numbers with optional percentage
      /^[+\-]?\d+([.,]\d+)?([A-Za-z]{1,3})?$/,  // Numbers with units
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,  // Emails
      /^https?:\/\//,  // URLs
      /^[A-Z]+$/,  // Short all-caps text
    ];
    
    return untranslatablePatterns?.some(pattern => pattern?.test(text?.trim()));
  }

  async queueTranslation(element, targetLanguage) {
    this.translationQueue?.push({ element, targetLanguage });
    if (!this.isProcessingQueue) {
      this.processTranslationQueue();
    }
  }

  async processTranslationQueue() {
    if (this.isProcessingQueue || this.translationQueue?.length === 0) return;
    
    this.isProcessingQueue = true;
    
    while (this.translationQueue?.length > 0) {
      const batch = this.translationQueue?.splice(0, 5); // Process in batches
      
      await Promise.all(batch?.map(async ({ element, targetLanguage }) => {
        try {
          if (element?.nodeType === Node.TEXT_NODE) {
            const originalText = element?.textContent?.trim();
            if (originalText && !this.isTranslatable(originalText, targetLanguage)) {
              const translated = await this.translateText(originalText, targetLanguage);
              if (translated && translated !== originalText) {
                element.textContent = translated;
              }
            }
          } else if (element?.nodeType === Node.ELEMENT_NODE) {
            await this.translateElement(element, targetLanguage);
          }
        } catch (error) {
          console.warn('Queue translation failed:', error);
        }
      }));
      
      // Small delay between batches
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.isProcessingQueue = false;
  }

  async translateElement(element, targetLanguage) {
    if (!element || this.excludeSelectors?.some(selector => {
      try {
        return element?.matches?.(selector) || element?.closest?.(selector);
      } catch (e) {
        return false;
      }
    })) {
      return;
    }

    // Translate attributes
    const attributesToTranslate = ['placeholder', 'title', 'aria-label', 'alt'];
    for (const attr of attributesToTranslate) {
      const value = element?.getAttribute(attr);
      if (value && !this.isTranslatable(value, targetLanguage)) {
        try {
          const translated = await this.translateText(value, targetLanguage);
          if (translated && translated !== value) {
            element?.setAttribute(attr, translated);
          }
        } catch (error) {
          console.warn(`Failed to translate ${attr}:`, error);
        }
      }
    }

    // Translate text content for specific elements
    const textElements = ['BUTTON', 'OPTION', 'LABEL', 'SPAN', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'A', 'DIV'];
    if (textElements?.includes(element?.tagName) && element?.children?.length === 0) {
      const textContent = element?.textContent?.trim();
      if (textContent && !this.isTranslatable(textContent, targetLanguage)) {
        try {
          const translated = await this.translateText(textContent, targetLanguage);
          if (translated && translated !== textContent) {
            element.textContent = translated;
          }
        } catch (error) {
          console.warn('Failed to translate text content:', error);
        }
      }
    }
  }

  getAllTextNodes(element = document.body) {
    const textNodes = [];
    const walker = document?.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node?.parentElement;
          if (!parent || this.excludeSelectors?.some(selector => {
            try {
              return parent?.closest(selector) !== null;
            } catch (e) {
              return false;
            }
          })) {
            return NodeFilter.FILTER_REJECT;
          }
          
          const text = node?.textContent?.trim();
          if (!text || text?.length < 2 || this.isTranslatable(text, 'any')) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    let node;
    while (node = walker?.nextNode()) {
      textNodes?.push(node);
    }
    
    return textNodes;
  }

  getTranslatableElements() {
    const selectors = [
      'input[placeholder]:not([data-translate="false"])',
      'textarea[placeholder]:not([data-translate="false"])',
      'button:not([data-translate="false"]):not([type="submit"]):not([type="button"])',
      'option:not([data-translate="false"])',
      '[title]:not([data-translate="false"])',
      '[aria-label]:not([data-translate="false"])',
      '[alt]:not([data-translate="false"])',
      'label:not([data-translate="false"])'
    ];
    
    return Array.from(document?.querySelectorAll(selectors?.join(', ')))?.filter(element => 
      !this.excludeSelectors?.some(selector => {
        try {
          return element?.closest(selector) !== null;
        } catch (e) {
          return false;
        }
      })
    );
  }

  async translatePage(targetLanguage, onProgress) {
    if (this.isTranslating) {
      console.warn('Translation already in progress');
      return;
    }
    
    this.isTranslating = true;
    let processedItems = 0;
    let totalItems = 0;
    
    try {
      // Prepare translation elements
      const textNodes = this.getAllTextNodes();
      const elements = this.getTranslatableElements();
      totalItems = textNodes?.length + elements?.length;
      
      if (totalItems === 0) {
        console.warn('No translatable content found');
        onProgress?.(100);
        return;
      }

      console.log(`Starting translation of ${totalItems} items to ${targetLanguage}`);

      // Translate text nodes in batches
      const batchSize = 10;
      for (let i = 0; i < textNodes?.length; i += batchSize) {
        const batch = textNodes?.slice(i, i + batchSize);
        
        await Promise.allSettled(batch?.map(async (node) => {
          try {
            const originalText = node?.textContent;
            if (originalText && !this.isTranslatable(originalText, targetLanguage)) {
              const translatedText = await this.translateText(originalText, targetLanguage);
              if (translatedText && translatedText !== originalText) {
                node.textContent = translatedText;
              }
            }
            processedItems++;
            onProgress?.((processedItems / totalItems) * 100);
          } catch (error) {
            console.warn('Text node translation failed:', error);
            processedItems++; // Count as processed to maintain progress
            onProgress?.((processedItems / totalItems) * 100);
          }
        }));
        
        // Prevent UI blocking
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Translate form elements and other translatable elements
      for (let i = 0; i < elements?.length; i += batchSize) {
        const batch = elements?.slice(i, i + batchSize);
        
        await Promise.allSettled(batch?.map(async (element) => {
          try {
            await this.translateElement(element, targetLanguage);
            processedItems++;
            onProgress?.((processedItems / totalItems) * 100);
          } catch (error) {
            console.warn('Element translation failed:', error);
            processedItems++; // Count as processed
            onProgress?.((processedItems / totalItems) * 100);
          }
        }));
        
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      // Setup observer for dynamic content
      this.setupMutationObserver(targetLanguage);
      
      console.log(`Translation completed. Processed ${processedItems}/${totalItems} items.`);
      
    } catch (error) {
      console.error('Page translation failed:', error);
      throw error;
    } finally {
      this.isTranslating = false;
    }
  }

  setupMutationObserver(targetLanguage) {
    if (this.mutationObserver) {
      this.mutationObserver?.disconnect();
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations?.forEach((mutation) => {
        if (mutation?.type === 'childList') {
          mutation?.addedNodes?.forEach((node) => {
            if (node?.nodeType === Node.ELEMENT_NODE) {
              // Queue new elements for translation
              this.queueTranslation(node, targetLanguage);
              
              // Also queue child text nodes
              const childTextNodes = this.getAllTextNodes(node);
              childTextNodes?.forEach(textNode => {
                this.queueTranslation(textNode, targetLanguage);
              });
            } else if (node?.nodeType === Node.TEXT_NODE) {
              this.queueTranslation(node, targetLanguage);
            }
          });
        }
      });
    });

    this.mutationObserver?.observe(document?.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['placeholder', 'title', 'aria-label', 'alt']
    });
    
    this.observerActive = true;
  }

  clearCache() {
    this.cache?.clear();
    console.log('Translation cache cleared');
  }

  disconnect() {
    if (this.mutationObserver) {
      this.mutationObserver?.disconnect();
      this.mutationObserver = null;
      this.observerActive = false;
    }
    this.translationQueue = [];
    this.isProcessingQueue = false;
  }

  getStats() {
    return {
      cacheSize: this.cache?.size,
      isTranslating: this.isTranslating,
      queueLength: this.translationQueue?.length,
      observerActive: this.observerActive,
      retryAttempts: this.retryAttempts
    };
  }
}

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('FR');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [translationService] = useState(() => new TranslationService());
  const [hasAutoTranslated, setHasAutoTranslated] = useState(false);

  const languages = [
    { code: 'FR', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
    { code: 'EN', name: 'English', flag: '🇺🇸', nativeName: 'English' },
    { code: 'DE', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
    { code: 'ES', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
    { code: 'IT', name: 'Italian', flag: '🇮🇹', nativeName: 'Italiano' },
    { code: 'PT', name: 'Portuguese', flag: '🇵🇹', nativeName: 'Português' },
    { code: 'NL', name: 'Dutch', flag: '🇳🇱', nativeName: 'Nederlands' },
    { code: 'RU', name: 'Russian', flag: '🇷🇺', nativeName: 'Русский' },
    { code: 'ZH', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
    { code: 'JA', name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' }
  ];

  const translateToLanguage = async (languageCode) => {
    if (languageCode === currentLanguage || isTranslating) {
      console.log(`Already in ${languageCode} or translation in progress`);
      return;
    }

    setIsTranslating(true);
    setTranslationProgress(0);

    try {
      console.log(`Starting translation to ${languageCode}`);
      
      await translationService?.translatePage(languageCode, (progress) => {
        setTranslationProgress(Math.round(progress));
      });
      
      setCurrentLanguage(languageCode);
      setHasAutoTranslated(true);
      
      // Store preference
      localStorage.setItem('preferred-language', languageCode);
      localStorage.setItem('has-auto-translated', 'true');
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { language: languageCode } 
      }));
      
      console.log(`Translation to ${languageCode} completed successfully`);
      
    } catch (error) {
      console.error('Translation failed:', error);
      
      // Dispatch error event
      window.dispatchEvent(new CustomEvent('translationError', { 
        detail: { 
          message: `La traduction vers ${languages?.find(l => l?.code === languageCode)?.nativeName} a échoué. Vérifiez votre connexion internet et réessayez.`,
          error: error?.message,
          code: languageCode
        } 
      }));
      
      throw error;
    } finally {
      setIsTranslating(false);
      setTranslationProgress(0);
    }
  };

  const getCurrentLanguage = () => {
    return languages?.find(lang => lang?.code === currentLanguage);
  };

  const resetTranslation = () => {
    translationService?.clearCache();
    translationService?.disconnect();
    localStorage.removeItem('preferred-language');
    localStorage.removeItem('has-auto-translated');
    setHasAutoTranslated(false);
    window.location?.reload(); // Hard reset
  };

  // Initialize translation on page load
  useEffect(() => {
    const initializeTranslation = async () => {
      const savedLanguage = localStorage.getItem('preferred-language');
      const hasTranslated = localStorage.getItem('has-auto-translated') === 'true';
      
      if (savedLanguage && languages?.some(lang => lang?.code === savedLanguage)) {
        setCurrentLanguage(savedLanguage);
        setHasAutoTranslated(hasTranslated);
        
        // Only auto-translate if we haven't before and saved language is not French
        if (!hasTranslated && savedLanguage !== 'FR') {
          setTimeout(async () => {
            try {
              await translateToLanguage('FR');
            } catch (error) {
              console.error('Initial auto-translation failed:', error);
            }
          }, 1500);
        }
      } else {
        // Default to French with auto-translation
        setTimeout(async () => {
          try {
            if (!hasAutoTranslated) {
              await translateToLanguage('FR');
            }
          } catch (error) {
            console.error('Default French translation failed:', error);
          }
        }, 1500);
      }
    };

    // Only initialize if we haven't auto-translated yet
    if (typeof window !== 'undefined') {
      initializeTranslation();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      translationService?.disconnect();
    };
  }, [translationService]);

  // Debug logging
  useEffect(() => {
    const interval = setInterval(() => {
      const stats = translationService?.getStats();
      if (stats?.isTranslating || stats?.queueLength > 0) {
        console.log('Translation stats:', stats);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [translationService]);

  const value = {
    currentLanguage,
    languages,
    isTranslating,
    translationProgress,
    hasAutoTranslated,
    translateToLanguage,
    getCurrentLanguage,
    resetTranslation,
    translationService
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;